import './FinanciarWizard.scss';

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { isMobile } from 'react-device-detect';

import { useTenant } from '../../context/TenantContext';
import { useFeatureFlags } from '../../context/FeatureFlagsContext';
import { BackButton, ContinueButton } from '../../components/buttons/Buttons';
import EntradaMoneda from '../../components/currencyInput/EntradaMoneda';
import LoadingScreen from '../../components/loading/LoadingScreen';
import ConsentBox from '../../components/consentBox/ConsentBox';
import { simulateCredit, confirmOption, updateClientData } from '../../services/apiService';
import {
  saveToSessionStorage,
  removeSessionStorage,
  getFromSessionStorage,
} from '../../services/storageService';
import { footAmoutMessage, validarPie } from '../../utils/formUtils';
import { cleanCurrencyValue } from '../../utils/formUtils';
import useTrackEvent from '../../utils/trackEvent';

const CUOTAS_VALUES = [6, 12, 18, 24, 30, 36, 42, 48, 60];

const FormCredito = ({ onNextStep, onPreviousStep, data }) => {
  const hasPushedPageView = useRef(false);
  const navigate = useNavigate();
  const { isMeliTenant } = useTenant();
  const { flags, isLoadingFlags } = useFeatureFlags();
  const { pushPageView, pushCustomEvent } = useTrackEvent();
  const parsedData = typeof data === 'string' ? JSON.parse(data) : data;
  const storedFormData = JSON.parse(sessionStorage.getItem('formData')) || {};
  const isConsentEnabled = flags.isConsentEnabled;
  const isAutoConfirmFlow = flags.isAutoConfirmFlow;
  const isTwoSteps = !isLoadingFlags && isAutoConfirmFlow;
  const isDirectAutoConfirmFlow = isMeliTenant || isTwoSteps;
  const executiveRut = !isLoadingFlags ? flags.executiveRut : undefined;
  const sellerRut = !isLoadingFlags ? flags.sellerRut : undefined;
  const dealerRut = !isLoadingFlags ? flags.dealerRut : undefined;
  const storeCode = !isLoadingFlags ? flags.storeCode : undefined;
  const storeCodeMeli = !isLoadingFlags ? flags.storeCodeMeli : undefined;
  const storeCodeTwoSteps = !isLoadingFlags ? flags.storeCodeTwoSteps : undefined;

  const [formData, setFormData] = useState({
    id: parsedData?.id || storedFormData?.id,
    rut: parsedData?.rut || storedFormData?.rut || '',
    dv: parsedData?.dv || storedFormData?.dv || '',
    name: parsedData?.name || storedFormData?.name || '',
    surnames: parsedData?.surnames || storedFormData?.surnames || '',
    phone: parsedData?.phone || storedFormData?.phone || '',
    email: parsedData?.email || storedFormData?.email || '',
    typeFinancing: parsedData?.typeFinancing || storedFormData?.typeFinancing || '',
    workerType: parsedData?.workerType || storedFormData?.workerType || '',
    salary: parsedData?.salary || storedFormData?.salary || 0,
    startWorkingDate: parsedData?.startWorkingDate || storedFormData?.startWorkingDate || '',
    carValue: parsedData?.carValue || storedFormData?.carValue || '',
    footAmount: parsedData?.footAmount || storedFormData?.footAmount || '',
    fee: parsedData?.fee || storedFormData?.fee || '',
    caryear: parsedData?.caryear || storedFormData?.caryear || '',
    consentChecked: parsedData?.consentChecked || storedFormData?.consentChecked || false,
  });

  const [selectedYear, setSelectedYear] = useState(storedFormData?.caryear || '');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [inputErrors, setInputErrors] = useState({
    carValue: '',
    footAmount: '',
    caryear: '',
  });

  useEffect(() => {
    const storedFormData = JSON.parse(sessionStorage.getItem('formData'));
    if (storedFormData) {
      setFormData(storedFormData);
      setSelectedYear(storedFormData.caryear || '');
    }

    if (!hasPushedPageView.current) {
      pushPageView(
        'vpv',
        'Crédito Automotríz',
        '2',
        '/atencion-normal/info-vehiculo',
        'Información de vehículo - Atención Normal'
      );

      hasPushedPageView.current = true;
    }
  }, []);

  useEffect(() => {
    sessionStorage.setItem('formData', JSON.stringify(formData));
  }, [formData]);

  const getYearRange = () => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth();
    const endYear = currentMonth >= 8 ? currentYear + 1 : currentYear;
    const startYear = endYear - 7;
    return Array.from({ length: endYear - startYear + 1 }, (_, i) => endYear - i);
  };

  const validateForm = () => {
    const carValue = parseFloat(String(formData.carValue).replace(/\D/g, ''));
    const footAmount = parseFloat(String(formData.footAmount).replace(/\D/g, ''));

    if (!carValue || carValue <= 0) {
      return 'Por favor, ingresa un valor válido para el vehículo.';
    }

    if (carValue.toString().length > 9) {
      return 'El valor del vehículo no puede exceder 8 dígitos.';
    }

    if (footAmount.toString().length > 8) {
      return 'El monto del pie no puede exceder 7 dígitos.';
    }

    if (!validarPie(footAmount, carValue, formData.fee, selectedYear)) {
      return footAmoutMessage(carValue, selectedYear, formData.fee);
    }

    if (!formData.fee || !CUOTAS_VALUES.includes(parseInt(formData.fee))) {
      return 'Por favor, selecciona un número de cuotas válido (6, 12, 18, 24, 30, 36, 42, 48).';
    }

    return null;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const rawValue = cleanCurrencyValue(value).toString();

    let maxLength = 15;
    if (name === 'carValue') maxLength = 9;
    if (name === 'footAmount') maxLength = 8;

    const limitedValue = rawValue.slice(0, maxLength);

    setFormData((prevData) => ({
      ...prevData,
      [name]: limitedValue,
    }));
    handleBlur({ target: { name, value: limitedValue } });
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setInputErrors((prevErrors) => ({
      ...prevErrors,
      [name]: value ? '' : 'Campo requerido',
    }));
  };

  const handleYearChange = (year) => {
    setSelectedYear(year);
    setFormData((prevData) => ({ ...prevData, caryear: year }));
  };

  const handleConsentChange = (e) => {
    setFormData((prevData) => ({ ...prevData, consentChecked: e.target.checked }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const eventData = {
      flujo: 'Crédito Automotríz',
      button_step_value: '2',
      step: '2',
      page: '/atencion-normal/info-vehiculo',
      PageName: 'Información de vehículo - Atención Normal',
      button_step_name: 'simular',
      version: 'v2',
    };
    pushCustomEvent('event_buttons', eventData);

    setError(null);

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsLoading(true);

    const updatedData = {
      ...formData,
      carValue: formData.carValue,
      footAmount: formData.footAmount,
      fee: formData.fee,
      caryear: formData.caryear,
    };

    saveToSessionStorage('formData', formData);

    try {
      const existingData = JSON.parse(sessionStorage.getItem('formData')) || {};
      const response = await updateClientData(updatedData.id, updatedData);
      if (response) {
        const simulationDataStored = getFromSessionStorage('simulationData');

        let simulationData = simulationDataStored ?? null;

        if (!simulationData) {
          simulationData = await simulateCredit({
            ...response,
            footAmount: formData.footAmount,
            uuId: existingData?.uuId || null,
            sourceName: isMeliTenant ? 'MercadoLibre' : 'Crediautos',
            channelRequest: isMeliTenant ? 'Mercadolibre' : `${updatedData.typeFinancing}_v2`,
            type: isMeliTenant ? 'CF' : null,
            storeCode: isMeliTenant ? storeCodeMeli : isTwoSteps ? storeCodeTwoSteps : storeCode,
            consentChecked: formData.consentChecked,
            executiveRut,
            sellerRut,
            dealerRut,
          });
        }

        saveToSessionStorage('formData', {
          uuId: simulationData?.simulationData.id,
          ...formData,
        });

        const simulationId = simulationData?.simulationData.id;

        if (isDirectAutoConfirmFlow) {
          saveToSessionStorage('simulationData', simulationData);

          const creditSimulation = simulationData?.simulationData.creditSimulation || [];
          const [firstOption] = Array.isArray(creditSimulation) ? creditSimulation : [];
          const confirmPayload = {
            ...formData,
            option: firstOption || null,
            simulationId,
            storeCode: isMeliTenant ? storeCodeMeli : isTwoSteps ? storeCodeTwoSteps : storeCode,
            executiveRut,
            sellerRut,
            dealerRut,
          };

          let confirmResult;
          try {
            confirmResult = await confirmOption(confirmPayload);
          } catch (e) {
            setError('¡Ups! No pudimos confirmar tu solicitud. Intenta nuevamente.');
            return;
          }

          const isConfirmed =
            confirmResult?.statusCode === 200 &&
            confirmResult?.confirmationData?.responseCode === 0;

          if (!isConfirmed) {
            setError('¡Ups! No pudimos confirmar tu solicitud. Intenta nuevamente.');
            return;
          }

          eventData.button_step_name = 'finalizar OK';
          pushCustomEvent('event_buttons', eventData);

          removeSessionStorage('simulationData');

          setIsLoading(false);
          navigate('/credito-automotriz-simple/solicitud-exitosa');
        } else {
          eventData.button_step_name = 'simular OK';
          pushCustomEvent('event_buttons', eventData);
          onNextStep(simulationData);
        }
      }
    } catch (error) {
      eventData.button_step_name = 'simular Error';
      pushCustomEvent('event_buttons', eventData);
      setError('¡Ups! Algo salió mal. Intenta nuevamente en unos segundos.');
    } finally {
      setIsLoading(false);
    }
  };

  const renderMoneyInputField = (label, name, value, placeholder, error) => (
    <div className="formControl-root">
      <label
        className={`inputLabel-root formLabel-root inputLabel-formControl inputLabel-outlined${
          error ? '-input-error' : ''
        }`}
      >
        {label} *
      </label>
      <div className="outlinedInput-root textField-root inputBase-root">
        <EntradaMoneda
          type="text"
          name={name}
          value={value}
          placeholder={`Ej: ${placeholder}`}
          className={`form-input${error ? '-input-error' : ''}`}
          onChange={handleInputChange}
          onBlur={handleBlur}
          required
        />
      </div>
      {error && <span className="error-required-input-field">{error}</span>}
    </div>
  );

  if (!isDirectAutoConfirmFlow && isLoading) {
    return <LoadingScreen />;
  }

  return (
    <div className={`financiar-wizard-credito-form ${isMobile ? '' : 'custom-width'}`}>
      <h1>Información del crédito a solicitar</h1>
      <p>Para continuar necesitamos conocer la siguiente información</p>
      <form onSubmit={handleSubmit}>
        {renderMoneyInputField(
          'Valor aproximado del vehículo',
          'carValue',
          formData.carValue,
          '25.000.000',
          inputErrors.carValue
        )}
        {renderMoneyInputField(
          'Monto del pie',
          'footAmount',
          formData.footAmount,
          '5.000.000',
          inputErrors.footAmount
        )}

        <div className="credito-input-container">
          <div className="formControl-root">
            <label className="inputLabel-root formLabel-root inputLabel-formControl inputLabel-outlined">
              Número de cuotas *
            </label>
            <div className="outlinedInput-root textField-root inputBase-root">
              <select
                name="fee"
                className="form-input-column"
                onChange={handleInputChange}
                value={formData.fee || ''}
                required
              >
                <option value="" hidden disabled>
                  Seleccionar número de cuotas
                </option>
                {CUOTAS_VALUES.map((cuota, index) => (
                  <option key={index} value={cuota}>
                    {cuota}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="formControl-root">
            <label className="inputLabel-root formLabel-root inputLabel-formControl inputLabel-outlined">
              Año del vehículo *
            </label>
            <div className="outlinedInput-root textField-root inputBase-root">
              <select
                name="caryear"
                required
                className="form-input-column"
                onChange={(e) => handleYearChange(e.target.value)}
                value={selectedYear}
              >
                <option value="" disabled hidden>
                  Seleccionar año
                </option>
                {getYearRange().map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {!isLoadingFlags && isConsentEnabled && (
          <ConsentBox checked={formData.consentChecked} onChange={handleConsentChange} />
        )}

        {error && <div className="error-msg">{error}</div>}

        <div className="nav-button-container">
          <BackButton onClick={onPreviousStep} />
          <ContinueButton
            type="submit"
            isLoading={isLoading}
            text={isDirectAutoConfirmFlow ? 'FINALIZAR' : 'SIMULAR'}
            disabled={!formData.consentChecked}
          />
        </div>
      </form>
    </div>
  );
};

export default FormCredito;
